// features/pos/components/KitchenView.tsx (COMPLETE - FULL FILE)
/**
 * Kitchen Display System (KDS)
 * 
 * Real-time order tracking for kitchen staff.
 * Features:
 * - Auto-refreshing order grid (every 10 seconds)
 * - Status management: New → Preparing → Ready → Served
 * - Priority indicators (RUSH, High, Normal)
 * - Time tracking (how long ago order was placed)
 * - Table number display
 * - Order notes display
 * - Cancel functionality
 * - Live/Paused toggle for auto-refresh
 * - Responsive grid layout
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { getKdsOrders, updateKdsStatus } from '../services/posApi';
import {
    ChefHat, ArrowLeft, Clock, CheckCircle, RefreshCw,
    Package, AlertTriangle, Flame, Timer, Utensils,
    Bell, BellOff, Play, Pause,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface KitchenViewProps {
    onBack: () => void;
    tenantId: string;
    branchId: string;
    backTitle?: string;
}

interface KdsOrder {
    id: string;
    tenantId: string;
    branchId: string;
    saleItemId: string;
    productName?: string;
    variantName?: string | null;
    quantity?: string;
    uomName?: string;
    tableName?: string | null;
    orderNumber?: string;
    status: 'new' | 'preparing' | 'ready' | 'served' | 'cancelled';
    priority: 'low' | 'normal' | 'high' | 'rush';
    notes: string | null;
    preparedBy: string | null;
    startedAt: string | null;
    completedAt: string | null;
    createdAt: string;
}

// ============================================================
// CONSTANTS
// ============================================================

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; border: string; icon: React.ElementType }> = {
    new: {
        label: 'NEW',
        color: 'text-red-400',
        bg: 'bg-red-500/10',
        border: 'border-red-500',
        icon: AlertTriangle,
    },
    preparing: {
        label: 'PREPARING',
        color: 'text-amber-400',
        bg: 'bg-amber-500/10',
        border: 'border-amber-500',
        icon: Timer,
    },
    ready: {
        label: 'READY',
        color: 'text-emerald-400',
        bg: 'bg-emerald-500/10',
        border: 'border-emerald-500',
        icon: CheckCircle,
    },
    served: {
        label: 'SERVED',
        color: 'text-slate-400',
        bg: 'bg-slate-500/10',
        border: 'border-slate-500',
        icon: Utensils,
    },
    cancelled: {
        label: 'CANCELLED',
        color: 'text-slate-500',
        bg: 'bg-slate-600/10',
        border: 'border-slate-600',
        icon: BellOff,
    },
};

const PRIORITY_CONFIG: Record<string, { label: string; variant: 'danger' | 'warning' | 'info' | 'neutral' }> = {
    rush: { label: 'RUSH', variant: 'danger' },
    high: { label: 'HIGH', variant: 'warning' },
    normal: { label: 'NORMAL', variant: 'info' },
    low: { label: 'LOW', variant: 'neutral' },
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export function KitchenView({ onBack, tenantId, branchId, backTitle = 'Back' }: KitchenViewProps) {
    const queryClient = useQueryClient();
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [soundEnabled, setSoundEnabled] = useState(true);

    // Fetch KDS orders
    const {
        data: orders,
        isLoading,
        isError,
        refetch,
        isFetching,
    } = useQuery({
        queryKey: ['kds-orders', tenantId, branchId],
        queryFn: async (): Promise<KdsOrder[]> => {
            const result = await getKdsOrders(tenantId, branchId);
            return Array.isArray(result) ? result : [];
        },
        enabled: !!tenantId && !!branchId,
        refetchInterval: autoRefresh ? 10000 : false, // Refresh every 10 seconds
        staleTime: 5000,
    });

    // Update KDS status mutation
    const updateMutation = useMutation({
        mutationFn: ({ kdsId, status }: { kdsId: string; status: string }) =>
            updateKdsStatus(tenantId, kdsId, status),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['kds-orders', tenantId, branchId] });
        },
    });

    // Play notification sound for new orders
    const playNotificationSound = useCallback(() => {
        if (!soundEnabled) return;
        try {
            const audio = new Audio('/sounds/new-order.mp3');
            audio.volume = 0.5;
            audio.play().catch(() => { });
        } catch { }
    }, [soundEnabled]);

    // Derived data
    const kdsOrders: KdsOrder[] = orders || [];

    // Filter out served and cancelled for main view
    const activeOrders = kdsOrders.filter(o => o.status !== 'served' && o.status !== 'cancelled');
    const completedOrders = kdsOrders.filter(o => o.status === 'served');

    // Stats
    const newCount = kdsOrders.filter(o => o.status === 'new').length;
    const preparingCount = kdsOrders.filter(o => o.status === 'preparing').length;
    const readyCount = kdsOrders.filter(o => o.status === 'ready').length;
    const totalActive = activeOrders.length;

    // Get relative time
    const getTimeAgo = (dateStr: string): string => {
        if (!dateStr) return '—';
        const diff = Date.now() - new Date(dateStr).getTime();
        const seconds = Math.floor(diff / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);

        if (seconds < 10) return 'Just now';
        if (seconds < 60) return `${seconds}s ago`;
        if (minutes < 60) return `${minutes}m ago`;
        if (hours < 24) return `${hours}h ${minutes % 60}m ago`;
        return `${Math.floor(hours / 24)}d ago`;
    };

    // Get elapsed time for display
    const getElapsedMinutes = (dateStr: string): number => {
        if (!dateStr) return 0;
        return Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    };

    // Check if order is taking too long (>15 minutes for rush, >30 for normal)
    const isOverdue = (order: KdsOrder): boolean => {
        const elapsed = getElapsedMinutes(order.createdAt);
        if (order.priority === 'rush' && elapsed > 10) return true;
        if (order.priority === 'high' && elapsed > 20) return true;
        if (elapsed > 30) return true;
        return false;
    };

    // ==================== LOADING STATE ====================
    if (isLoading && !orders) {
        return (
            <div className="h-screen bg-slate-950 flex items-center justify-center">
                <div className="text-center">
                    <Spinner size="xl" variant="white" />
                    <p className="text-white/60 mt-4">Loading Kitchen Display...</p>
                </div>
            </div>
        );
    }

    // ==================== ERROR STATE ====================
    if (isError) {
        return (
            <div className="h-screen bg-slate-950 flex items-center justify-center">
                <ErrorState
                    title="Failed to load orders"
                    message="Could not connect to the kitchen display system."
                    onRetry={() => refetch()}
                />
            </div>
        );
    }

    // ==================== RENDER ====================
    return (
        <div className="h-screen bg-slate-950 text-white flex flex-col overflow-hidden">
            {/* ========== HEADER ========== */}
            <div className="px-6 py-4 bg-slate-900 border-b border-slate-800 flex items-center justify-between flex-shrink-0 shadow-lg">
                {/* Left: Back + Brand */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={onBack}
                        className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
                        type="button"
                        title={backTitle}
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div className="p-2 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 shadow-lg shadow-red-500/20">
                        <ChefHat size={22} className="text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold">Kitchen Display</h1>
                        <p className="text-xs text-slate-400">
                            {totalActive} active order{totalActive !== 1 ? 's' : ''}
                            {isFetching && autoRefresh && (
                                <span className="ml-2 text-emerald-400 animate-pulse">● Live</span>
                            )}
                        </p>
                    </div>
                </div>

                {/* Center: Stats */}
                <div className="hidden md:flex items-center gap-4">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-sm font-bold text-red-400">{newCount} New</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <Timer size={14} className="text-amber-400" />
                        <span className="text-sm font-bold text-amber-400">{preparingCount} Preparing</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                        <CheckCircle size={14} className="text-emerald-400" />
                        <span className="text-sm font-bold text-emerald-400">{readyCount} Ready</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-700/50 border border-slate-600/30 rounded-lg">
                        <Utensils size={14} className="text-slate-400" />
                        <span className="text-sm font-bold text-slate-400">{completedOrders.length} Served</span>
                    </div>
                </div>

                {/* Right: Controls */}
                <div className="flex items-center gap-2">
                    {/* Sound Toggle */}
                    <button
                        onClick={() => setSoundEnabled(!soundEnabled)}
                        className={clsx(
                            'p-2 rounded-lg transition-all text-xs font-bold',
                            soundEnabled ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-700 text-slate-500'
                        )}
                        type="button"
                        title={soundEnabled ? 'Sound ON' : 'Sound OFF'}
                    >
                        {soundEnabled ? <Bell size={16} /> : <BellOff size={16} />}
                    </button>

                    {/* Auto-Refresh Toggle */}
                    <button
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        className={clsx(
                            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all',
                            autoRefresh
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                : 'bg-slate-700 text-slate-400 border border-slate-600'
                        )}
                        type="button"
                    >
                        {autoRefresh ? (
                            <>
                                <RefreshCw size={12} className="animate-spin" />
                                Live
                            </>
                        ) : (
                            <>
                                <Pause size={12} />
                                Paused
                            </>
                        )}
                    </button>

                    {/* Manual Refresh */}
                    <button
                        onClick={() => refetch()}
                        disabled={isFetching}
                        className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 disabled:opacity-50 transition-colors"
                        type="button"
                        title="Refresh now"
                    >
                        <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
                    </button>
                </div>
            </div>

            {/* ========== MOBILE STATS BAR ========== */}
            <div className="md:hidden px-4 py-2 bg-slate-900/50 flex items-center gap-2 overflow-x-auto flex-shrink-0">
                <span className="text-xs text-red-400 font-bold">{newCount} New</span>
                <span className="text-slate-600">·</span>
                <span className="text-xs text-amber-400 font-bold">{preparingCount} Prep</span>
                <span className="text-slate-600">·</span>
                <span className="text-xs text-emerald-400 font-bold">{readyCount} Ready</span>
                <span className="text-slate-600">·</span>
                <span className="text-xs text-slate-400 font-bold">{completedOrders.length} Done</span>
            </div>

            {/* ========== ORDER GRID ========== */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
                {kdsOrders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-600">
                        <motion.div
                            animate={{ rotate: [0, -10, 10, -10, 0] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            <ChefHat size={80} className="mb-6 text-slate-700" />
                        </motion.div>
                        <p className="text-xl font-bold text-slate-500">No Orders Yet</p>
                        <p className="text-sm text-slate-600 mt-2">New orders will appear here automatically</p>
                        <p className="text-xs text-slate-700 mt-1">
                            {autoRefresh ? '● Auto-refresh is ON' : '○ Auto-refresh is OFF - click Refresh'}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Active Orders */}
                        {activeOrders.length > 0 && (
                            <div className="mb-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <Flame size={16} className="text-orange-400" />
                                    <h2 className="text-sm font-bold text-slate-300 uppercase tracking-wider">
                                        Active Orders ({activeOrders.length})
                                    </h2>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {activeOrders.map((order) => {
                                        const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.new;
                                        const overdue = isOverdue(order);
                                        const elapsed = getElapsedMinutes(order.createdAt);

                                        return (
                                            <motion.div
                                                key={order.id}
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.95 }}
                                                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                                                className={clsx(
                                                    'rounded-2xl border-2 overflow-hidden transition-all',
                                                    statusConfig.border,
                                                    statusConfig.bg,
                                                    overdue && 'ring-2 ring-red-500/50 shadow-lg shadow-red-500/20'
                                                )}
                                            >
                                                {/* Order Header */}
                                                <div className="px-4 py-3 flex items-center justify-between border-b border-slate-700/30">
                                                    <div className="flex items-center gap-2">
                                                        <statusConfig.icon size={16} className={statusConfig.color} />
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm font-bold text-white">
                                                                    {order.orderNumber || `#${order.id.slice(0, 8).toUpperCase()}`}
                                                                </span>
                                                                {order.priority !== 'normal' && (
                                                                    <Badge variant={PRIORITY_CONFIG[order.priority]?.variant || 'info'} size="sm">
                                                                        {PRIORITY_CONFIG[order.priority]?.label || order.priority}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                <Clock size={10} className="text-slate-500" />
                                                                <span className={clsx(
                                                                    'text-xs',
                                                                    overdue ? 'text-red-400 font-bold' : 'text-slate-400'
                                                                )}>
                                                                    {getTimeAgo(order.createdAt)}
                                                                    {overdue && ' · OVERDUE'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <span className={clsx(
                                                        'text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider',
                                                        statusConfig.bg, statusConfig.color
                                                    )}>
                                                        {statusConfig.label}
                                                    </span>
                                                </div>

                                                {/* Order Content */}
                                                <div className="p-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center flex-shrink-0">
                                                            <Package size={18} className="text-slate-400" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-bold text-white text-sm">
                                                                {order.productName || 'Menu Item'}
                                                            </p>
                                                            {order.variantName && (
                                                                <p className="text-xs text-slate-400 mt-0.5">
                                                                    {order.variantName}
                                                                </p>
                                                            )}
                                                            <div className="flex items-center gap-2 mt-1">
                                                                <span className="text-xs text-slate-500">
                                                                    Qty: {order.quantity || 1} {order.uomName || ''}
                                                                </span>
                                                                {order.tableName && (
                                                                    <>
                                                                        <span className="text-slate-700">·</span>
                                                                        <span className="text-xs text-slate-500">
                                                                            Table: {order.tableName}
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {overdue && (
                                                            <div className="flex-shrink-0">
                                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 rounded-lg text-[10px] font-bold">
                                                                    <AlertTriangle size={10} />
                                                                    {elapsed}m
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Order Notes */}
                                                    {order.notes && (
                                                        <div className="mt-3 p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                                                            <p className="text-xs text-amber-300 flex items-start gap-1.5">
                                                                <span className="flex-shrink-0">📝</span>
                                                                <span>{order.notes}</span>
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Action Buttons */}
                                                <div className="px-4 py-3 border-t border-slate-700/30 flex gap-2">
                                                    {order.status === 'new' && (
                                                        <button
                                                            onClick={() => updateMutation.mutate({ kdsId: order.id, status: 'preparing' })}
                                                            disabled={updateMutation.isPending}
                                                            className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl text-xs font-bold hover:bg-amber-600 transition-all disabled:opacity-50 flex items-center justify-center gap-1.5"
                                                            type="button"
                                                        >
                                                            <Play size={12} /> Start Preparing
                                                        </button>
                                                    )}
                                                    {order.status === 'preparing' && (
                                                        <button
                                                            onClick={() => updateMutation.mutate({ kdsId: order.id, status: 'ready' })}
                                                            disabled={updateMutation.isPending}
                                                            className="flex-1 py-2.5 bg-emerald-500 text-white rounded-xl text-xs font-bold hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center justify-center gap-1.5"
                                                            type="button"
                                                        >
                                                            <CheckCircle size={12} /> Mark Ready
                                                        </button>
                                                    )}
                                                    {order.status === 'ready' && (
                                                        <button
                                                            onClick={() => updateMutation.mutate({ kdsId: order.id, status: 'served' })}
                                                            disabled={updateMutation.isPending}
                                                            className="flex-1 py-2.5 bg-blue-500 text-white rounded-xl text-xs font-bold hover:bg-blue-600 transition-all disabled:opacity-50 flex items-center justify-center gap-1.5"
                                                            type="button"
                                                        >
                                                            <Utensils size={12} /> Mark Served
                                                        </button>
                                                    )}
                                                    {(order.status === 'new' || order.status === 'preparing') && (
                                                        <button
                                                            onClick={() => {
                                                                if (window.confirm('Cancel this order?')) {
                                                                    updateMutation.mutate({ kdsId: order.id, status: 'cancelled' });
                                                                }
                                                            }}
                                                            className="px-3 py-2.5 bg-slate-700 text-slate-400 rounded-xl text-xs font-medium hover:bg-rose-500/20 hover:text-rose-400 transition-all"
                                                            type="button"
                                                            title="Cancel order"
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Completed Orders (Collapsible) */}
                        {completedOrders.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <CheckCircle size={16} className="text-emerald-400" />
                                    <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider">
                                        Recently Served ({completedOrders.length})
                                    </h2>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 opacity-60">
                                    {completedOrders.slice(0, 8).map((order) => (
                                        <div
                                            key={order.id}
                                            className="rounded-xl border border-slate-700 bg-slate-800/30 p-3"
                                        >
                                            <div className="flex items-center gap-2">
                                                <CheckCircle size={14} className="text-emerald-500" />
                                                <span className="text-sm font-medium text-slate-400">
                                                    {order.productName || 'Item'}
                                                </span>
                                                <span className="ml-auto text-xs text-slate-500">
                                                    {getTimeAgo(order.createdAt)}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ========== FOOTER ========== */}
            <div className="px-6 py-2 bg-slate-900 border-t border-slate-800 flex items-center justify-between flex-shrink-0 text-xs text-slate-600">
                <span>KDS v1.0</span>
                <span>
                    {autoRefresh ? 'Auto-refresh every 10s' : 'Auto-refresh paused'}
                    {updateMutation.isPending && ' · Updating...'}
                </span>
                <span>{new Date().toLocaleTimeString()}</span>
            </div>
        </div>
    );
}